import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ClipboardList, Target, Plus, Trash2, ChevronDown, ChevronRight, Save } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { clientService } from '../services/ClientService';
import { assessmentService } from '../services/AssessmentService';
import { treatmentPlanService } from '../services/TreatmentPlanService';
import { Client } from '../models/Client';
import { Assessment, AssessmentDomainScore } from '../models/Assessment';
import {
  TreatmentPlan,
  TreatmentGoal,
  TreatmentTarget,
  GoalAssessmentRef,
} from '../models/TreatmentPlan';

const genId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
const iso = (d: Date) => new Date(d).toISOString().slice(0, 10);
const fmtDate = (d: Date) => d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });

const card: React.CSSProperties = {
  backgroundColor: 'var(--color-white)',
  borderRadius: 'var(--radius-xl)',
  boxShadow: 'var(--shadow-md)',
  border: '1px solid var(--color-border)',
  padding: 'var(--spacing-5)',
};
const input: React.CSSProperties = {
  width: '100%',
  padding: 'var(--spacing-2) var(--spacing-3)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--text-sm)',
  fontFamily: 'var(--font-family-base)',
  color: 'var(--color-text-primary)',
  backgroundColor: 'var(--color-white)',
};
const label: React.CSSProperties = {
  display: 'block',
  fontSize: 'var(--text-xs)',
  fontWeight: 'var(--font-semibold)',
  color: 'var(--color-text-secondary)',
  marginBottom: '3px',
};
const btnPrimary: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 'var(--spacing-2)',
  padding: 'var(--spacing-2) var(--spacing-4)', backgroundColor: 'var(--color-primary)',
  color: 'var(--color-white)', border: 'none', borderRadius: 'var(--radius-md)',
  fontWeight: 'var(--font-semibold)', cursor: 'pointer', fontSize: 'var(--text-sm)',
};
const btnGhost: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 'var(--spacing-2)',
  padding: 'var(--spacing-2) var(--spacing-3)', background: 'transparent',
  color: 'var(--color-primary)', border: '1px solid var(--color-primary)',
  borderRadius: 'var(--radius-md)', fontWeight: 'var(--font-medium)', cursor: 'pointer', fontSize: 'var(--text-sm)',
};
const iconBtn: React.CSSProperties = { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-error)', display: 'inline-flex' };
const sectionTitle: React.CSSProperties = { fontSize: 'var(--text-xl)', fontWeight: 'var(--font-bold)', color: 'var(--color-text-primary)' };

/* ------------------------------- page --------------------------------- */

export const ClinicalRecord: React.FC = () => {
  const { user } = useAuth();
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();

  const [client, setClient] = useState<Client | null>(null);
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [plans, setPlans] = useState<TreatmentPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const authorized = !user || user.role === 'supervisor' || user.role === 'admin';

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const [c, a, p] = await Promise.all([
        clientService.getClient(clientId),
        assessmentService.getByClient(clientId),
        treatmentPlanService.getByClient(clientId),
      ]);
      setClient(c);
      setAssessments(a);
      setPlans(p);
    } catch (e) {
      console.error('ClinicalRecord load error:', e);
      setError('Could not load the clinical record.');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!authorized) {
    return (
      <div style={{ maxWidth: 640, margin: '0 auto' }}>
        <div style={{ ...card, textAlign: 'center', color: 'var(--color-text-secondary)' }}>
          This screen is for supervisors and admins.
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      <button
        onClick={() => navigate('/clients')}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--spacing-2)', background: 'none', border: 'none', color: 'var(--color-primary)', cursor: 'pointer', marginBottom: 'var(--spacing-4)', fontWeight: 'var(--font-medium)' }}
      >
        <ArrowLeft size={18} /> Back to Clients
      </button>

      {loading ? (
        <div style={{ ...card, textAlign: 'center', color: 'var(--color-text-secondary)' }}>Loading…</div>
      ) : !client ? (
        <div style={{ ...card, textAlign: 'center', color: 'var(--color-text-secondary)' }}>Client not found.</div>
      ) : (
        <>
          <h1 style={{ ...sectionTitle, fontSize: 'var(--text-2xl)', marginBottom: '4px' }}>
            {client.firstName} {client.lastName}
          </h1>
          <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-6)' }}>
            Clinical Record — assessments &amp; treatment plan
          </div>

          {error && (
            <div style={{ ...card, marginBottom: 'var(--spacing-5)', borderColor: 'var(--color-error)', color: 'var(--color-error)' }}>{error}</div>
          )}

          <AssessmentsSection
            clientId={client.id}
            assessments={assessments}
            administeredBy={user?.id || ''}
            onSaved={load}
          />

          <div style={{ height: 'var(--spacing-8)' }} />

          <TreatmentPlansSection
            client={client}
            plans={plans}
            assessments={assessments}
            createdBy={user?.id || ''}
            onSaved={load}
          />
        </>
      )}
    </div>
  );
};

/* --------------------------- Assessments ------------------------------ */

const AssessmentsSection: React.FC<{
  clientId: string;
  assessments: Assessment[];
  administeredBy: string;
  onSaved: () => void;
}> = ({ clientId, assessments, administeredBy, onSaved }) => {
  const [recording, setRecording] = useState(false);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('VB-MAPP');
  const [administeredDate, setAdministeredDate] = useState(iso(new Date()));
  const [domains, setDomains] = useState<AssessmentDomainScore[]>([]);
  const [clinicalNotes, setClinicalNotes] = useState('');
  const [recommendedActions, setRecommendedActions] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);

  const addDomain = () => setDomains((d) => [...d, { domain: '', itemScores: [], domainTotal: 0, domainMax: 0 }]);
  const removeDomain = (i: number) => setDomains((d) => d.filter((_, x) => x !== i));
  const setDomainName = (i: number, v: string) => setDomains((d) => d.map((dm, x) => (x === i ? { ...dm, domain: v } : dm)));
  const addItem = (i: number) =>
    setDomains((d) => d.map((dm, x) => (x === i ? { ...dm, itemScores: [...dm.itemScores, { itemCode: '', itemDescription: '', score: 0, maxScore: 0, notes: null }] } : dm)));
  const setItem = (di: number, ii: number, field: string, v: string | number) =>
    setDomains((d) =>
      d.map((dm, x) => {
        if (x !== di) return dm;
        const items = dm.itemScores.map((it, y) => (y === ii ? { ...it, [field]: v } : it));
        const domainTotal = items.reduce((s, it) => s + (Number(it.score) || 0), 0);
        const domainMax = items.reduce((s, it) => s + (Number(it.maxScore) || 0), 0);
        return { ...dm, itemScores: items, domainTotal, domainMax };
      })
    );
  const removeItem = (di: number, ii: number) =>
    setDomains((d) => d.map((dm, x) => (x === di ? { ...dm, itemScores: dm.itemScores.filter((_, y) => y !== ii) } : dm)));

  const overallScore = domains.reduce((s, dm) => s + dm.domainTotal, 0);
  const overallMax = domains.reduce((s, dm) => s + dm.domainMax, 0);

  const reset = () => {
    setName('VB-MAPP'); setAdministeredDate(iso(new Date())); setDomains([]);
    setClinicalNotes(''); setRecommendedActions(''); setRecording(false);
  };

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await assessmentService.create(clientId, {
        assessmentName: name.trim(),
        administeredBy,
        administeredDate: new Date(administeredDate),
        status: 'complete',
        domainScores: domains,
        overallScore: overallMax > 0 ? overallScore : null,
        overallMax: overallMax > 0 ? overallMax : null,
        clinicalNotes,
        recommendedActions: recommendedActions || null,
      });
      reset();
      onSaved();
    } catch (e) {
      console.error('save assessment error:', e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--spacing-4)' }}>
        <h2 style={{ ...sectionTitle, display: 'inline-flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
          <ClipboardList size={20} style={{ color: 'var(--color-primary)' }} /> Assessments
        </h2>
        {!recording && <button style={btnPrimary} onClick={() => setRecording(true)}><Plus size={16} /> Record Assessment</button>}
      </div>

      {/* Recorder */}
      {recording && (
        <div style={{ ...card, marginBottom: 'var(--spacing-4)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 'var(--spacing-3)', marginBottom: 'var(--spacing-3)' }}>
            <div>
              <label style={label}>Assessment</label>
              <input style={input} value={name} onChange={(e) => setName(e.target.value)} placeholder="VB-MAPP, ABLLS-R, AFLS, custom…" list="assessment-names" />
              <datalist id="assessment-names">
                <option value="VB-MAPP" /><option value="ABLLS-R" /><option value="AFLS" /><option value="PEAK" />
              </datalist>
            </div>
            <div>
              <label style={label}>Administered</label>
              <input type="date" style={input} value={administeredDate} onChange={(e) => setAdministeredDate(e.target.value)} />
            </div>
          </div>

          {domains.map((dm, di) => (
            <div key={di} style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: 'var(--spacing-3)', marginBottom: 'var(--spacing-2)' }}>
              <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'flex-end', marginBottom: 'var(--spacing-2)' }}>
                <div style={{ flex: 1 }}>
                  <label style={label}>Domain</label>
                  <input style={input} value={dm.domain} onChange={(e) => setDomainName(di, e.target.value)} placeholder="Mand, Tact, Listener Responding…" />
                </div>
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap', paddingBottom: '8px' }}>
                  {dm.domainTotal}/{dm.domainMax}
                </div>
                <button style={iconBtn} onClick={() => removeDomain(di)} title="Remove domain"><Trash2 size={16} /></button>
              </div>
              {dm.itemScores.map((it, ii) => (
                <div key={ii} style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 60px 60px 24px', gap: '6px', marginBottom: '6px' }}>
                  <input style={input} value={it.itemCode} onChange={(e) => setItem(di, ii, 'itemCode', e.target.value)} placeholder="Code" />
                  <input style={input} value={it.itemDescription} onChange={(e) => setItem(di, ii, 'itemDescription', e.target.value)} placeholder="Label (not licensed content)" />
                  <input type="number" style={input} value={it.score} onChange={(e) => setItem(di, ii, 'score', Number(e.target.value))} placeholder="Score" />
                  <input type="number" style={input} value={it.maxScore} onChange={(e) => setItem(di, ii, 'maxScore', Number(e.target.value))} placeholder="Max" />
                  <button style={iconBtn} onClick={() => removeItem(di, ii)}><Trash2 size={14} /></button>
                </div>
              ))}
              <button style={{ ...btnGhost, marginTop: '4px' }} onClick={() => addItem(di)}><Plus size={14} /> Item</button>
            </div>
          ))}
          <button style={btnGhost} onClick={addDomain}><Plus size={14} /> Domain</button>

          <div style={{ marginTop: 'var(--spacing-3)' }}>
            <label style={label}>Clinical Notes</label>
            <textarea style={{ ...input, resize: 'vertical' }} rows={3} value={clinicalNotes} onChange={(e) => setClinicalNotes(e.target.value)} />
          </div>
          <div style={{ marginTop: 'var(--spacing-2)' }}>
            <label style={label}>Recommended Actions</label>
            <textarea style={{ ...input, resize: 'vertical' }} rows={2} value={recommendedActions} onChange={(e) => setRecommendedActions(e.target.value)} />
          </div>

          <div style={{ display: 'flex', gap: 'var(--spacing-3)', marginTop: 'var(--spacing-4)', alignItems: 'center' }}>
            <button style={{ ...btnPrimary, opacity: saving || !name.trim() ? 0.6 : 1 }} onClick={save} disabled={saving || !name.trim()}>
              <Save size={16} /> {saving ? 'Saving…' : 'Save Assessment'}
            </button>
            <button style={{ ...btnGhost, border: 'none', color: 'var(--color-text-secondary)' }} onClick={reset}>Cancel</button>
            <span style={{ marginLeft: 'auto', fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
              Overall {overallScore}/{overallMax}
            </span>
          </div>
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)', marginTop: 'var(--spacing-2)' }}>
            Assessments are immutable once saved. Record a new administration for updated scores.
          </p>
        </div>
      )}

      {/* List */}
      {assessments.length === 0 && !recording ? (
        <div style={{ ...card, textAlign: 'center', color: 'var(--color-text-secondary)' }}>No assessments recorded yet.</div>
      ) : (
        <div style={{ display: 'grid', gap: 'var(--spacing-3)' }}>
          {assessments.map((a) => (
            <div key={a.id} style={card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={() => setExpanded(expanded === a.id ? null : a.id)}>
                <div>
                  <span style={{ fontWeight: 'var(--font-semibold)', color: 'var(--color-text-primary)' }}>{a.assessmentName}</span>
                  <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', marginLeft: 'var(--spacing-3)' }}>{fmtDate(a.administeredDate)}</span>
                </div>
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
                  {a.overallScore != null ? `${a.overallScore}/${a.overallMax}` : `${a.domainScores.length} domains`} {expanded === a.id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </span>
              </div>
              {expanded === a.id && (
                <div style={{ marginTop: 'var(--spacing-3)', fontSize: 'var(--text-sm)' }}>
                  {a.domainScores.map((dm, i) => (
                    <div key={i} style={{ marginBottom: '4px' }}>
                      <strong>{dm.domain}</strong> — {dm.domainTotal}/{dm.domainMax}
                      {dm.itemScores.length > 0 && (
                        <span style={{ color: 'var(--color-text-secondary)' }}> ({dm.itemScores.map((it) => `${it.itemCode}:${it.score}/${it.maxScore}`).join(', ')})</span>
                      )}
                    </div>
                  ))}
                  {a.clinicalNotes && <div style={{ marginTop: '6px', color: 'var(--color-text-secondary)', whiteSpace: 'pre-wrap' }}>{a.clinicalNotes}</div>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

/* ------------------------- Treatment Plans ---------------------------- */

const emptyTarget = (): TreatmentTarget => ({
  targetId: genId(),
  description: '',
  status: 'not-started',
  masteryCriteria: { percentCorrect: 80, consecutiveSessions: 3, minimumTrials: 10 },
  currentBaseline: null,
  instructionType: null,
  promptHierarchy: null,
  programs: [],
});
const emptyGoal = (): TreatmentGoal => ({
  goalId: genId(),
  description: '',
  status: 'active',
  createdDate: new Date(),
  targetDate: null,
  assessmentRef: null,
  targets: [emptyTarget()],
});

const TreatmentPlansSection: React.FC<{
  client: Client;
  plans: TreatmentPlan[];
  assessments: Assessment[];
  createdBy: string;
  onSaved: () => void;
}> = ({ client, plans, assessments, createdBy, onSaved }) => {
  const [editing, setEditing] = useState<TreatmentPlan | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);

  const startNew = () => {
    setIsNew(true);
    setEditing({
      id: '', clientId: client.id, companyId: client.companyId, createdBy,
      status: 'draft', effectiveDate: new Date(), reviewDate: new Date(Date.now() + 90 * 24 * 3600 * 1000),
      previousPlanId: null, goals: [emptyGoal()], createdAt: new Date(), updatedAt: new Date(),
    });
  };
  // Copy-on-write is safe here: every mutation below uses .map/spread, so the
  // original `plans` objects are never mutated. A shallow copy is enough.
  const startEdit = (p: TreatmentPlan) => { setIsNew(false); setEditing({ ...p }); };

  const patch = (u: Partial<TreatmentPlan>) => setEditing((p) => (p ? { ...p, ...u } : p));
  const setGoal = (gi: number, u: Partial<TreatmentGoal>) =>
    setEditing((p) => (p ? { ...p, goals: p.goals.map((g, i) => (i === gi ? { ...g, ...u } : g)) } : p));
  const setGoalTargets = (gi: number, fn: (t: TreatmentTarget[]) => TreatmentTarget[]) =>
    setEditing((p) => (p ? { ...p, goals: p.goals.map((g, i) => (i === gi ? { ...g, targets: fn(g.targets) } : g)) } : p));
  const setTarget = (gi: number, ti: number, u: Partial<TreatmentTarget>) =>
    setGoalTargets(gi, (targets) => targets.map((t, i) => (i === ti ? { ...t, ...u } : t)));

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      if (isNew) {
        await treatmentPlanService.create(client.id, client.companyId, {
          createdBy, status: editing.status, effectiveDate: editing.effectiveDate,
          reviewDate: editing.reviewDate, previousPlanId: editing.previousPlanId, goals: editing.goals,
        });
      } else {
        await treatmentPlanService.update(client.id, editing.id, {
          goals: editing.goals, status: editing.status, reviewDate: editing.reviewDate, effectiveDate: editing.effectiveDate,
        });
      }
      setEditing(null);
      onSaved();
    } catch (e) {
      console.error('save plan error:', e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--spacing-4)' }}>
        <h2 style={{ ...sectionTitle, display: 'inline-flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
          <Target size={20} style={{ color: 'var(--color-primary)' }} /> Treatment Plans
        </h2>
        {!editing && <button style={btnPrimary} onClick={startNew}><Plus size={16} /> Create Plan</button>}
      </div>

      {editing ? (
        <div style={{ ...card }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--spacing-3)', marginBottom: 'var(--spacing-4)' }}>
            <div>
              <label style={label}>Status</label>
              <select style={input} value={editing.status} onChange={(e) => patch({ status: e.target.value as TreatmentPlan['status'] })}>
                <option value="draft">Draft</option><option value="active">Active</option><option value="completed">Completed</option><option value="revised">Revised</option>
              </select>
            </div>
            <div>
              <label style={label}>Effective Date</label>
              <input type="date" style={input} value={iso(editing.effectiveDate)} onChange={(e) => patch({ effectiveDate: new Date(e.target.value) })} />
            </div>
            <div>
              <label style={label}>Review Date</label>
              <input type="date" style={input} value={iso(editing.reviewDate)} onChange={(e) => patch({ reviewDate: new Date(e.target.value) })} />
            </div>
          </div>

          {editing.goals.map((g, gi) => (
            <GoalEditor
              key={g.goalId}
              goal={g}
              assessments={assessments}
              onChange={(u) => setGoal(gi, u)}
              onRemove={() => patch({ goals: editing.goals.filter((_, i) => i !== gi) })}
              addTarget={() => setGoalTargets(gi, (t) => [...t, emptyTarget()])}
              setTarget={(ti, u) => setTarget(gi, ti, u)}
              removeTarget={(ti) => setGoalTargets(gi, (t) => t.filter((_, i) => i !== ti))}
            />
          ))}
          <button style={btnGhost} onClick={() => patch({ goals: [...editing.goals, emptyGoal()] })}><Plus size={14} /> Goal</button>

          <div style={{ display: 'flex', gap: 'var(--spacing-3)', marginTop: 'var(--spacing-4)' }}>
            <button style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }} onClick={save} disabled={saving}><Save size={16} /> {saving ? 'Saving…' : isNew ? 'Create Plan' : 'Save Plan'}</button>
            <button style={{ ...btnGhost, border: 'none', color: 'var(--color-text-secondary)' }} onClick={() => setEditing(null)}>Cancel</button>
          </div>
        </div>
      ) : plans.length === 0 ? (
        <div style={{ ...card, textAlign: 'center', color: 'var(--color-text-secondary)' }}>No treatment plans yet.</div>
      ) : (
        <div style={{ display: 'grid', gap: 'var(--spacing-3)' }}>
          {plans.map((p) => (
            <div key={p.id} style={{ ...card, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={() => startEdit(p)}>
              <div>
                <span style={{ fontWeight: 'var(--font-semibold)', textTransform: 'capitalize' }}>{p.status}</span>
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', marginLeft: 'var(--spacing-3)' }}>
                  Effective {fmtDate(p.effectiveDate)} · {p.goals.length} goal{p.goals.length === 1 ? '' : 's'}
                </span>
              </div>
              <ChevronRight size={16} style={{ color: 'var(--color-text-secondary)' }} />
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

/* ----------------------------- Goal editor ---------------------------- */

const GoalEditor: React.FC<{
  goal: TreatmentGoal;
  assessments: Assessment[];
  onChange: (u: Partial<TreatmentGoal>) => void;
  onRemove: () => void;
  addTarget: () => void;
  setTarget: (ti: number, u: Partial<TreatmentTarget>) => void;
  removeTarget: (ti: number) => void;
}> = ({ goal, assessments, onChange, onRemove, addTarget, setTarget, removeTarget }) => {
  const refAssessment = assessments.find((a) => a.id === goal.assessmentRef?.assessmentId) || null;
  const refDomain = refAssessment?.domainScores.find((d) => d.domain === goal.assessmentRef?.domain) || null;

  const linkAssessment = (assessmentId: string) => {
    if (!assessmentId) return onChange({ assessmentRef: null });
    const a = assessments.find((x) => x.id === assessmentId);
    if (!a) return;
    onChange({ assessmentRef: { assessmentId, assessmentName: a.assessmentName, domain: '', itemCode: '', scoreAtCreation: 0, administeredDate: a.administeredDate } });
  };
  const linkDomain = (domain: string) => {
    if (!goal.assessmentRef) return;
    onChange({ assessmentRef: { ...goal.assessmentRef, domain, itemCode: '', scoreAtCreation: 0 } as GoalAssessmentRef });
  };
  const linkItem = (itemCode: string) => {
    if (!goal.assessmentRef || !refDomain) return;
    const item = refDomain.itemScores.find((it) => it.itemCode === itemCode);
    onChange({ assessmentRef: { ...goal.assessmentRef, itemCode, scoreAtCreation: item ? item.score : refDomain.domainTotal } as GoalAssessmentRef });
  };

  return (
    <div style={{ border: '1px solid var(--color-border)', borderLeft: '3px solid var(--color-primary)', borderRadius: 'var(--radius-md)', padding: 'var(--spacing-3)', marginBottom: 'var(--spacing-3)' }}>
      <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <label style={label}>Goal</label>
          <input style={input} value={goal.description} onChange={(e) => onChange({ description: e.target.value })} placeholder="Clinical goal statement" />
        </div>
        <div style={{ width: 130 }}>
          <label style={label}>Status</label>
          <select style={input} value={goal.status} onChange={(e) => onChange({ status: e.target.value as TreatmentGoal['status'] })}>
            <option value="active">Active</option><option value="achieved">Achieved</option><option value="modified">Modified</option><option value="discontinued">Discontinued</option>
          </select>
        </div>
        <button style={{ ...iconBtn, paddingTop: 20 }} onClick={onRemove} title="Remove goal"><Trash2 size={16} /></button>
      </div>

      {/* Assessment link (the clinical chain) */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--spacing-2)', marginTop: 'var(--spacing-2)' }}>
        <div>
          <label style={label}>Linked Assessment</label>
          <select style={input} value={goal.assessmentRef?.assessmentId || ''} onChange={(e) => linkAssessment(e.target.value)}>
            <option value="">— None —</option>
            {assessments.map((a) => <option key={a.id} value={a.id}>{a.assessmentName} ({fmtDate(a.administeredDate)})</option>)}
          </select>
        </div>
        <div>
          <label style={label}>Domain</label>
          <select style={input} value={goal.assessmentRef?.domain || ''} disabled={!refAssessment} onChange={(e) => linkDomain(e.target.value)}>
            <option value="">—</option>
            {refAssessment?.domainScores.map((d, i) => <option key={i} value={d.domain}>{d.domain}</option>)}
          </select>
        </div>
        <div>
          <label style={label}>Item (score {goal.assessmentRef?.scoreAtCreation ?? '—'})</label>
          <select style={input} value={goal.assessmentRef?.itemCode || ''} disabled={!refDomain} onChange={(e) => linkItem(e.target.value)}>
            <option value="">— domain total —</option>
            {refDomain?.itemScores.map((it, i) => <option key={i} value={it.itemCode}>{it.itemCode}</option>)}
          </select>
        </div>
      </div>

      {/* Targets */}
      <div style={{ marginTop: 'var(--spacing-3)' }}>
        {goal.targets.map((t, ti) => (
          <div key={t.targetId} style={{ backgroundColor: 'var(--color-gray-50)', borderRadius: 'var(--radius-md)', padding: 'var(--spacing-2) var(--spacing-3)', marginBottom: '6px' }}>
            <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <label style={label}>Target</label>
                <input style={input} value={t.description} onChange={(e) => setTarget(ti, { description: e.target.value })} placeholder="e.g. Will tact 10 common objects" />
              </div>
              <div style={{ width: 110 }}>
                <label style={label}>Status</label>
                <select style={input} value={t.status} onChange={(e) => setTarget(ti, { status: e.target.value as TreatmentTarget['status'] })}>
                  <option value="not-started">Not started</option><option value="in-progress">In progress</option><option value="mastered">Mastered</option><option value="on-hold">On hold</option>
                </select>
              </div>
              <button style={iconBtn} onClick={() => removeTarget(ti)}><Trash2 size={14} /></button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1.4fr', gap: '6px', marginTop: '6px' }}>
              <div><label style={label}>% correct</label><input type="number" style={input} value={t.masteryCriteria.percentCorrect} onChange={(e) => setTarget(ti, { masteryCriteria: { ...t.masteryCriteria, percentCorrect: Number(e.target.value) } })} /></div>
              <div><label style={label}>consec. sessions</label><input type="number" style={input} value={t.masteryCriteria.consecutiveSessions} onChange={(e) => setTarget(ti, { masteryCriteria: { ...t.masteryCriteria, consecutiveSessions: Number(e.target.value) } })} /></div>
              <div><label style={label}>min. trials</label><input type="number" style={input} value={t.masteryCriteria.minimumTrials} onChange={(e) => setTarget(ti, { masteryCriteria: { ...t.masteryCriteria, minimumTrials: Number(e.target.value) } })} /></div>
              <div><label style={label}>Instruction</label><input style={input} value={t.instructionType || ''} onChange={(e) => setTarget(ti, { instructionType: e.target.value || null })} placeholder="DTT, NET…" /></div>
            </div>
          </div>
        ))}
        <button style={{ ...btnGhost, fontSize: 'var(--text-xs)', padding: '4px 10px' }} onClick={addTarget}><Plus size={13} /> Target</button>
      </div>
    </div>
  );
};

export default ClinicalRecord;
